import React, { useState } from "react";
import logo from "../images/EnvironmentalLabsLogoHorizontal.png";
import profile from "../images/profile.png";
import { Link, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocalDrinkOutlinedIcon from "@mui/icons-material/LocalDrinkOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BRAND } from "../styles/brand";

const NAV_BLUE = BRAND.primary;
const NAV_BLUE_SOFT = BRAND.primarySoft;
const NAV_BLUE_BORDER = BRAND.primaryBorder;
const NAV_BLUE_SHADOW = BRAND.primaryShadow;

export default function Header({
  logout,
  isMultiClient,
  clientType,
  clientName,
  clearClient,
  accountNumber,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const menuOpen = Boolean(anchorEl);

  const navigationItems = [
    { label: "Dashboard", route: "/dashboard", icon: <DashboardOutlinedIcon fontSize="small" /> },
    { label: "Lab Orders", route: "/lab-orders", icon: <ScienceOutlinedIcon fontSize="small" /> },    
    { label: "Bottle Orders", route: "/bottle-orders", icon: <LocalDrinkOutlinedIcon fontSize="small" /> },
    { label: "Invoices", route: "/invoices", icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
    { label: "Client Profile", route: "/client", icon: <PersonOutlineIcon fontSize="small" /> },
    
    
    ...(clientType?.toLowerCase().includes("etk")
      ? [
          { label: "Water Profile Comparison", route: "/water-profile-comparison", icon: <CompareArrowsIcon fontSize="small" /> },
          { label: "Water Profiles", route: "/water-profiles", icon: <WaterDropOutlinedIcon fontSize="small" /> },
        ]
      : []),
  ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: "#ffffff",
        borderBottom: "1px solid #e1e8ed",
        boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: "72px !important", px: { xs: 2, md: 3 } }}>

        {/* Logo */}
        <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
          <Box
            component="img"
            src={logo}
            alt="ELI Logo"
            sx={{ height: 42, width: "auto", transition: "transform 0.2s", "&:hover": { transform: "scale(1.02)" } }}
          />
        </Box>

        {/* Nav Links */}
        {accountNumber && (
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, flex: 1, justifyContent: "center" }}>
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.route;
              return (
                <Button
                  key={item.route}
                  component={Link}
                  to={item.route}
                  startIcon={item.icon}
                  disableRipple={false}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1,
                    color: isActive ? "white" : NAV_BLUE,
                    bgcolor: isActive ? NAV_BLUE : "transparent",
                    boxShadow: isActive ? `0 6px 16px ${NAV_BLUE_SHADOW}` : "none",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "white",
                      bgcolor: NAV_BLUE,
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${NAV_BLUE_SHADOW}`,
                    },
                    "& .MuiButton-startIcon": { mr: 0.6 },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Right Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          {isMultiClient && (
            <Button
              onClick={clearClient}
              startIcon={<HomeOutlinedIcon />}
              variant="contained"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                bgcolor: NAV_BLUE,
                px: 2,
                "&:hover": { bgcolor: BRAND.primaryDark, transform: "translateY(-1px)", boxShadow: `0 6px 16px ${NAV_BLUE_SHADOW}` },
                transition: "all 0.2s",
              }}
            >
              Home
            </Button>
          )}

          {/* User Menu Trigger */}
          <Tooltip title="Account">
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                px: 1.5,
                py: 0.75,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                bgcolor: "#f7fafc",
                transition: "all 0.2s",
                "&:hover": { bgcolor: "#edf2f7", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-1px)" },
              }}
            >
              <Avatar
                src={profile}
                alt="Client"
                sx={{
                  width: 38,
                  height: 38,
                  border: `2px solid ${NAV_BLUE}`,
                  bgcolor: "white",
                  "& img": { objectFit: "contain", p: "3px" },
                }}
              />
              <KeyboardArrowDownIcon
                sx={{
                  color: "#718096",
                  fontSize: "1rem",
                  transition: "transform 0.3s",
                  transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>
          </Tooltip>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  mt: 1.5,
                  minWidth: 300,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                },
              },
            }}
          >
            {/* Account */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Avatar
                src={profile}
                alt="Client"
                sx={{
                  width: 44,
                  height: 44,
                  border: `2px solid ${NAV_BLUE}`,
                  bgcolor: "white",
                  "& img": { objectFit: "contain", p: "3px" },
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} color={NAV_BLUE} fontSize="1rem" noWrap>
                  {clientName}
                </Typography>
                {accountNumber && (
                  <Chip
                    label={`Account: ${accountNumber}`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: NAV_BLUE_SOFT,
                      color: NAV_BLUE,
                      border: `1px solid ${NAV_BLUE_BORDER}`,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Divider />

            {/* Sign Out */}
            <MenuItem
              onClick={() => { setAnchorEl(null); logout(); }}
              sx={{
                py: 1.25,
                px: 2.5,
                color: "error.main",
                "&:hover": { bgcolor: "error.light", color: "error.contrastText" },
                "&:hover .MuiListItemIcon-root": { color: "inherit" },
              }}
            >
              <ListItemIcon sx={{ color: "error.main", minWidth: 36 }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Sign Out"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}